import type { ReactNode } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { useBackNavigation } from '@repo/ui/hooks/use-back-navigation';

export interface ResourceNotFoundProps {
  /**
   * The type of resource that wasn't found (e.g., "Course", "Event", "Template")
   */
  resourceType: string;
  /**
   * The route to navigate back to (e.g., ROUTES.COURSES, ROUTES.DISCOUNT_DEALS)
   */
  backRoute: string;
  /**
   * Optional custom title. Defaults to "{resourceType} Not Found"
   */
  title?: string;
  /**
   * Optional custom message. Defaults to helpful suggestion
   */
  message?: string;
  /**
   * Optional custom back button text. Defaults to "Back to {resourceType}s"
   */
  backButtonText?: string;
  /**
   * Optional retry callback for recoverable errors (network issues, etc.)
   */
  onRetry?: () => void;
  /**
   * Optional additional actions (e.g., search, homepage link)
   */
  additionalActions?: ReactNode;
  /**
   * Optional data test id for testing
   */
  dataTestId?: string;
  /**
   * Optional data test id for the title heading (h2)
   */
  titleDataTestId?: string;
}

export function ResourceNotFound({
  resourceType,
  backRoute,
  title,
  message,
  backButtonText,
  onRetry,
  additionalActions,
  dataTestId = 'resource-not-found',
  titleDataTestId,
}: ResourceNotFoundProps) {
  const goBack = useBackNavigation(backRoute);

  const defaultTitle = `${resourceType} Not Found`;
  const defaultMessage = `This ${resourceType.toLowerCase()} may have been removed or you may have followed a broken link.`;
  const defaultBackButtonText = `Back to ${resourceType}s`;

  return (
    <section aria-live="polite" data-testid={dataTestId}>
      <SDSFlexbox
        direction="column"
        gap="content-large"
        alignItems="center"
        justifyContent="center"
        fullWidth
        style={{
          padding: 'var(--padding-content-large)',
          minHeight: '400px',
          textAlign: 'center',
        }}
      >
        <SDSFlexbox direction="column" gap="text-large" alignItems="center" style={{ maxWidth: '600px' }}>
          <SDSText type="h2" text={title || defaultTitle} color="text-primary" dataTestId={titleDataTestId} />
          <SDSText type="body-large" text={message || defaultMessage} color="text-secondary" />
        </SDSFlexbox>

        <SDSFlexbox direction="column" gap="text-medium" alignItems="center">
          {onRetry && (
            <SDSButton
              text="Try Again"
              onClick={onRetry}
              ariaLabel="Retry loading resource"
              dataTestId={DataTestIds.COMMON.CONTENT_AREA.RESOURCE_NOT_FOUND_RETRY}
              mode="outline"
            />
          )}
          <SDSButton
            text={backButtonText || defaultBackButtonText}
            onClick={goBack}
            ariaLabel={backButtonText || defaultBackButtonText}
            dataTestId={DataTestIds.COMMON.CONTENT_AREA.RESOURCE_NOT_FOUND_BACK}
          />
          {additionalActions}
        </SDSFlexbox>
      </SDSFlexbox>
    </section>
  );
}
